import { useState } from 'react'
import { SegmentControls } from 'builder/components/SegmentControls'
import Icon from 'builder/components/Icon'
import { DashboardBuilder } from 'builder/components/Icon/24x24'
import { useUser } from 'builder/hooks/useUser'
import { UserSettings } from 'builder/components/Navigation/components/UserSettings'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { DownloadButton } from 'builder/components/PreviewPanel/components/DownloadButton'
import { EditorTitleCompact } from 'builder/components/EditorTitle'
import LanguageSelect from 'builder/components/LanguageSelect'
import { useI18n } from 'builder/hooks/useI18n'
import { FORMATS } from 'builder/modules/constants'
import { DownloadDocumentHandler, ResumeEditorStore } from 'builder/modules/resumeEditor'
import {
  TabsContainer,
  SidebarToggleButton,
  DownloadButtonContainer,
  UserSettingsContainer,
  ResumeTitleContainer,
  TitleWrapper,
  LanguageSelectContainer,
  TabTextContainer,
  TabBadge,
  StyledSegmentControl,
} from './styles'
import { useTabNavigation, useResumeNameChange } from './hooks'

type Props = {
  resumeState: ResumeEditorStore
  handleDownloadDocument: DownloadDocumentHandler
  showResumeDistributionModal?: (format: FORMATS) => void
  handleShareDocument?: () => void
  onSidebarToggle?: (isOpen: boolean) => void
  isSidebarOpen: boolean
  isRbpStepFlowActive?: boolean
  locales: Array<{ id: string; name: string }>
  onLocaleSelect: (locale: string) => void
  isConversationalBuilderOpen?: boolean
}

export const TopNavBarDesktop = (props: Props) => {
  const {
    resumeState,
    handleDownloadDocument,
    showResumeDistributionModal,
    handleShareDocument,
    onSidebarToggle,
    isRbpStepFlowActive,
    locales,
    onLocaleSelect,
    isConversationalBuilderOpen,
    isSidebarOpen,
  } = props

  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false)
  const [isTitleEditing, setIsTitleEditing] = useState(false)
  const user = useUser()
  const isOnline = useTypedSelector(state => state.application.isOnline)
  const { i18n } = useI18n()

  const { resume, editorPanelType } = resumeState
  const { handleTabChange, getTabItems } = useTabNavigation()
  const { handleResumeNameChange } = useResumeNameChange()

  const handleSidebarToggle = () => {
    onSidebarToggle?.(!isSidebarOpen)
  }

  const tabItems = getTabItems()

  return (
    <TabsContainer $isAiWriterOpen={isConversationalBuilderOpen}>
      {!isRbpStepFlowActive && (
        <SidebarToggleButton
          onClick={handleSidebarToggle}
          data-testid="toggle-sidebar-navigation-button"
        >
          {isSidebarOpen ? <Icon.CloseLarge /> : <DashboardBuilder />}
        </SidebarToggleButton>
      )}
      {!isRbpStepFlowActive && (
        <ResumeTitleContainer>
          <TitleWrapper>
            <EditorTitleCompact
              onChange={handleResumeNameChange}
              value={resume?.name ? resume.name : i18n.t('builder.resume_editor.untitled')}
              onEditingChange={setIsTitleEditing}
            />
          </TitleWrapper>
          <LanguageSelectContainer $isEditing={isTitleEditing} data-editing={isTitleEditing}>
            <LanguageSelect
              selected={resume?.locale || 'en'}
              onSelect={onLocaleSelect}
              options={locales}
            />
          </LanguageSelectContainer>
        </ResumeTitleContainer>
      )}
      <SegmentControls>
        {tabItems.map(item => (
          <StyledSegmentControl
            key={item.id}
            $isActive={editorPanelType === item.id}
            onClick={() => handleTabChange(item.id)}
          >
            <TabTextContainer>
              {item.text}
              {item.id === 'tailoring' && <TabBadge>NEW</TabBadge>}
            </TabTextContainer>
          </StyledSegmentControl>
        ))}
      </SegmentControls>
      {!isRbpStepFlowActive && (
        <DownloadButtonContainer>
          <DownloadButton
            handleDownloadDocument={handleDownloadDocument}
            showResumeDistributionModal={showResumeDistributionModal}
            handleShareDocument={handleShareDocument}
            menuPosition="bottom"
            menuAlign="right"
          />
        </DownloadButtonContainer>
      )}
      {isOnline && user && (
        <UserSettingsContainer>
          <UserSettings user={user} isOpen={isUserDropdownOpen} onToggle={setIsUserDropdownOpen} />
        </UserSettingsContainer>
      )}
    </TabsContainer>
  )
}
